/**
 * The single rule for "is PostHog allowed to initialize" - deliberately a
 * strict allowlist (only these two exact production hostnames), the same
 * pattern `QaMode.isQaModeAllowedHost` uses, rather than a denylist of
 * known non-production hosts. That means every non-production
 * environment is safe by default without needing to be individually
 * remembered here - local dev, `vitest run` (no `window` at all - see
 * `resolveAnalyticsEnabled` in `posthogClient.ts`), a Netlify Deploy
 * Preview (`deploy-preview-N--snake-arenaz.netlify.app`), and the
 * project's own Netlify subdomain (`snake-arenaz.netlify.app`) itself are
 * all denied simply by not being one of the two names below - never by an
 * explicit exclusion.
 *
 * Pure and framework-free on purpose: no `window`/`import.meta` access
 * here, so this exact decision is unit-testable without a DOM or a Vite
 * env. `?qa=` QA modes never need a separate carve-out either - they're
 * only ever reachable on hosts this allowlist already denies (see
 * `QaMode.isQaModeAllowedHost`), so no QA run can ever be a production
 * analytics run.
 */
const PRODUCTION_HOSTNAMES = new Set(["snakescore.com", "www.snakescore.com"]);

/**
 * Exported so other modules that need "is this really the production
 * site" (e.g. `src/daily/shareUrl.ts`, which must never fabricate a
 * `snakescore.com` share link from a Deploy Preview) can reuse the exact
 * same allowlist instead of maintaining a second copy that could drift
 * from this one.
 */
export function isProductionHostname(hostname: string | null | undefined): boolean {
  return Boolean(hostname) && PRODUCTION_HOSTNAMES.has(hostname!.toLowerCase());
}

export interface ShouldEnableAnalyticsInput {
  /** A production build - i.e. Vite's `import.meta.env.PROD`, never re-derived from `MODE` or a hostname guess. */
  isProd: boolean;
  /** Whether `VITE_POSTHOG_KEY` was actually supplied at build time - never the key's value itself, which this function has no reason to see. */
  hasKey: boolean;
  hostname: string | null | undefined;
}

export function shouldEnableAnalytics(input: ShouldEnableAnalyticsInput): boolean {
  if (!input.isProd || !input.hasKey) return false;
  return isProductionHostname(input.hostname);
}
